import { useMemo } from 'react';
import { Option, SortOption } from '@common-fe/common-fe';

import { useDocumentOcrStore, useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { getFullAddress } from '@/utils';

const MAX_PROVIDER_OPTIONS = 3;

export interface ProviderOptionMetadata extends Record<string, string> {
  providerTitleSort: string;
  providerIdSort: string;
  providerAddress: string;
}

export const SORT_OPTIONS: SortOption[] = [
  {
    key: 'az',
    title: 'A-Z',
    field: 'providerTitleSort',
    direction: 'asc',
  },
  {
    key: 'recent',
    title: 'Recently added',
    field: 'providerIdSort',
    direction: 'desc',
  },
];

interface Props {
  customProviderId?: string;
}

const useProviderOptions = ({ customProviderId }: Props) => {
  const { ocrDocument } = useDocumentOcrStore();
  const { values } = useMyMoneyFormStore();

  const suggestedProviderOptions = useMemo((): Option<string>[] => {
    if (!ocrDocument?.providers?.length) {
      return [];
    }

    const suggestedProviders = ocrDocument?.providers?.filter((provider) => !!provider?.isSuggested && provider?.name);

    return suggestedProviders
      ?.slice(0, MAX_PROVIDER_OPTIONS)
      ?.map((suggestedProvider) => ({
        key: suggestedProvider?.id,
        title: suggestedProvider?.name,
        value: suggestedProvider?.id,
      }));
  }, [ocrDocument?.providers]);

  const providerOptions = useMemo((): Option<string, ProviderOptionMetadata>[] => {
    if (!ocrDocument?.providers?.length) {
      return [];
    }

    const notSuggestedProviders = ocrDocument?.providers?.filter((provider) => !provider?.isSuggested && provider?.name);

    return notSuggestedProviders
      ?.map((notSuggestedProvider) => ({
        key: notSuggestedProvider?.id,
        title: notSuggestedProvider?.name,
        value: notSuggestedProvider?.id,
        groupKey: 'All providers',
        metadata: {
          providerTitleSort: notSuggestedProvider?.name || '',
          providerIdSort: notSuggestedProvider?.id || '',
          providerAddress: getFullAddress(notSuggestedProvider?.address),
        },
      }));
  }, [ocrDocument?.providers]);

  const activeProviderOption = useMemo(() => providerOptions?.find((providerOption) => providerOption?.key === customProviderId),
    [customProviderId, providerOptions]);

  const activeSuggestedProviderOption = useMemo(() => suggestedProviderOptions
    ?.find((provider) => {
      if (!provider?.key || !customProviderId) {
        return false;
      }
      return provider?.key === customProviderId;
    }), [customProviderId, suggestedProviderOptions]);

  const provider = useMemo(() => ocrDocument?.providers?.find((provider) => provider?.id === values?.providerId),
    [ocrDocument?.providers, values?.providerId]);

  return {
    suggestedProviderOptions,
    providerOptions,
    activeProviderOption,
    activeSuggestedProviderOption,
    provider,
  };
};

export default useProviderOptions;
